/**
 * StrategicComparison — premium 4-block competitive section for the Landing.
 *
 * Block A — SaaS visual comparison table (AgenteLead Pro vs 8 competitors)
 * Block B — Battlecard executive comparison
 * Block C — Strategic 2D quadrant (Gartner-style)
 * Block D — Workflow / pipeline visual + KPIs + final CTA
 *
 * Theme: dark mode premium · neon purple accents · glassmorphism · grid.
 * i18n: PT / EN / ES driven by the `lang` prop coming from Landing.jsx.
 */
import { useEffect, useRef, useState } from "react";
import { Check, Minus, Crown, Trophy, Sparkles, Zap, MapPin, Bot, Workflow,
  MessageCircle, Send, BarChart3, Search, Wand2, ChevronRight, Target,
  Users as UsersIcon, Star, Layers, Cpu, Rocket, X } from "lucide-react";


/**
 * Single-shot IntersectionObserver hook — toggles `visible=true` the first
 * time the ref enters the viewport, then disconnects so we never re-trigger
 * the entrance animation while the user scrolls back up.
 */
function useReveal(threshold = 0.18) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      // SSR / older browsers — render visible immediately.
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e?.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, { threshold });
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}


function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 800ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms,
                     transform 800ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: "opacity, transform",
      }}>
      {children}
    </div>
  );
}

const T = {
  en: {
    // Block A
    aTitle: "AgenteLead Pro vs The Competition",
    aSubtitle: "The complete AI-powered platform to find, engage and close more local leads — faster.",
    aBadge: "Best All-in-One",
    aBadgeSub: "AI Sales Operating System",
    legendStrong: "Strong", legendPartial: "Partial / Limited", legendNo: "Not available",
    aClickHint: "Click any capability to see how AgenteLead Pro replaces multiple tools",
    aModalReplaces: "Replaces", aModalClose: "Close",
    // Block B
    bTitle: "Why AgenteLead Pro Wins More Deals",
    bSubtitle: "Side-by-side comparison of key differentiators",
    bCallout: "AgenteLead Pro combines Google Maps lead generation, AI audits, multi-channel outreach and pipeline automation in one platform.",
    bIdealUserTitle: "Ideal user",
    bIdealUserBody: "Agencies, consultants, SDRs and local service businesses that want predictable revenue.",
    bKey: "Key takeaway",
    // Block C
    cTitle: "Where AgenteLead Pro Stands",
    cSubtitle: "The AI-powered lead generation landscape",
    cAxisY: "AI & Automation Capabilities",
    cAxisX: "Simplicity & Ease of Use",
    cQ: { leaders: "Leaders", strong: "Strong Contender", powerful: "Powerful but Complex", easy: "Easy but Limited", limited: "Limited Impact" },
    // Block D
    dTitle: "The AI Sales Operating System for Local Leads",
    dSub: "Find, Engage, Close — Automatically.",
    dCtaTitle: "Ready to Grow Your Pipeline?",
    dCtaSub: "Join the operators turning Google Maps into a predictable lead engine.",
    dCta: "Start with AgenteLead Pro",
  },
  pt: {
    aTitle: "AgenteLead Pro vs A Concorrência",
    aSubtitle: "A plataforma completa com IA para encontrar, engajar e fechar mais leads locais — rápido.",
    aBadge: "Melhor All-in-One",
    aBadgeSub: "AI Sales Operating System",
    legendStrong: "Completo", legendPartial: "Parcial / Limitado", legendNo: "Não disponível",
    aClickHint: "Clique em qualquer capability para ver como o AgenteLead Pro substitui múltiplas ferramentas",
    aModalReplaces: "Substitui", aModalClose: "Fechar",
    bTitle: "Por que o AgenteLead Pro Fecha Mais Negócios",
    bSubtitle: "Comparação lado a lado dos diferenciais críticos",
    bCallout: "AgenteLead Pro reúne prospecção Google Maps, auditorias com IA, outreach multicanal e pipeline automatizado em uma única plataforma.",
    bIdealUserTitle: "Usuário ideal",
    bIdealUserBody: "Agências, consultores, SDRs e negócios locais que querem receita previsível.",
    bKey: "Conclusão",
    cTitle: "Onde o AgenteLead Pro Está",
    cSubtitle: "O cenário de geração de leads com IA",
    cAxisY: "Capacidade de IA & Automação",
    cAxisX: "Simplicidade & Facilidade de Uso",
    cQ: { leaders: "Líderes", strong: "Forte Contendor", powerful: "Poderoso mas Complexo", easy: "Fácil mas Limitado", limited: "Impacto Limitado" },
    dTitle: "O AI Sales Operating System para Leads Locais",
    dSub: "Encontre, Engaje, Feche — Automaticamente.",
    dCtaTitle: "Pronto para Crescer seu Pipeline?",
    dCtaSub: "Junte-se aos operadores que transformam o Google Maps em uma máquina previsível de leads.",
    dCta: "Começar com AgenteLead Pro",
  },
  es: {
    aTitle: "AgenteLead Pro vs La Competencia",
    aSubtitle: "La plataforma completa con IA para encontrar, comprometer y cerrar más leads locales — más rápido.",
    aBadge: "Mejor Todo-en-Uno",
    aBadgeSub: "AI Sales Operating System",
    legendStrong: "Completo", legendPartial: "Parcial / Limitado", legendNo: "No disponible",
    aClickHint: "Haz clic en cualquier capability para ver cómo AgenteLead Pro reemplaza múltiples herramientas",
    aModalReplaces: "Reemplaza", aModalClose: "Cerrar",
    bTitle: "Por qué AgenteLead Pro Cierra Más Tratos",
    bSubtitle: "Comparación lado a lado de los diferenciadores clave",
    bCallout: "AgenteLead Pro combina prospección con Google Maps, auditorías con IA, outreach multicanal y pipeline automatizado en una sola plataforma.",
    bIdealUserTitle: "Usuario ideal",
    bIdealUserBody: "Agencias, consultores, SDRs y negocios locales que buscan ingresos predecibles.",
    bKey: "Conclusión",
    cTitle: "Dónde está AgenteLead Pro",
    cSubtitle: "El panorama de generación de leads con IA",
    cAxisY: "Capacidades de IA y Automatización",
    cAxisX: "Simplicidad y Facilidad de Uso",
    cQ: { leaders: "Líderes", strong: "Fuerte Contendiente", powerful: "Potente pero Complejo", easy: "Fácil pero Limitado", limited: "Impacto Limitado" },
    dTitle: "El AI Sales Operating System para Leads Locales",
    dSub: "Encuentra, Compromete, Cierra — Automáticamente.",
    dCtaTitle: "¿Listo para Crecer tu Pipeline?",
    dCtaSub: "Únete a los operadores que convierten Google Maps en una máquina predecible de leads.",
    dCta: "Comenzar con AgenteLead Pro",
  },
};

// 9 columns total — 8 vendors + AgenteLead Pro highlighted.
// `logo` references either an internal asset (AgenteLead Pro robot) or a
// simpleicons.org CDN slug+colour. Falsy values render the text-only header.
const VENDORS = [
  { key: "alp",      name: "AgenteLead Pro", featured: true,
    customLogo: "/logos/agentelead-pro-dark-bg.png" },
  { key: "apollo",   name: "Apollo.io",       logo: null },
  { key: "instantly", name: "Instantly.ai",   logo: "intercom/FACC15" },
  { key: "close",    name: "Close CRM",       logo: null },
  { key: "clay",     name: "Clay",            logo: null },
  { key: "ghl",      name: "GoHighLevel",     logo: "googletagmanager/22D3EE" },
  { key: "lemlist",  name: "Lemlist",         logo: "letterboxd/14B8A6" },
  { key: "d7",       name: "D7 Lead Finder",  logo: "googleads/EF4444" },
  { key: "brightlocal", name: "BrightLocal",  logo: "googlemaps/F472B6" },
];


function VendorMark({ vendor }) {
  // Dark-bg logo for AgenteLead Pro, monochrome simpleicons for the rest.
  if (vendor.customLogo) {
    return (
      <img src={vendor.customLogo} alt={vendor.name}
        className="w-7 h-7 object-contain mx-auto mb-1"
        onError={(e) => { e.currentTarget.style.display = "none"; }} />
    );
  }
  if (!vendor.logo) return null;
  return (
    <img src={`https://cdn.simpleicons.org/${vendor.logo}`}
      alt="" aria-hidden
      className="w-5 h-5 object-contain mx-auto mb-1 opacity-85"
      onError={(e) => { e.currentTarget.style.display = "none"; }} />
  );
}

// Capability matrix — 0=no, 1=partial, 2=strong. `deepDive` (i18n) is
// rendered inside the click-to-open modal explaining how AgenteLead Pro
// replaces 3-4 competitor tools for that capability.
const CAPS = [
  { key: "maps",     i18n: { en: "Google Maps Prospecting",  pt: "Prospecção Google Maps",      es: "Prospección Google Maps" },
    icon: MapPin, vals: { alp: 2, apollo: 0, instantly: 0, close: 0, clay: 0, ghl: 0, lemlist: 0, d7: 2, brightlocal: 2 },
    deepDive: { en: "Pull thousands of high-intent local businesses straight from Google Maps in one click — replacing D7 Lead Finder + BrightLocal scraping while keeping CRM + outreach in the same workspace.",
                pt: "Puxe milhares de negócios locais com alta intenção direto do Google Maps em um clique — substituindo D7 Lead Finder + BrightLocal e mantendo CRM + outreach no mesmo workspace.",
                es: "Extrae miles de negocios locales de alta intención directamente de Google Maps con un clic — reemplazando D7 Lead Finder + BrightLocal y manteniendo CRM + outreach en el mismo espacio." } },
  { key: "audit",    i18n: { en: "AI Business Audits",       pt: "Auditorias com IA",           es: "Auditorías con IA" },
    icon: Wand2, vals: { alp: 2, apollo: 1, instantly: 0, close: 0, clay: 2, ghl: 0, lemlist: 0, d7: 0, brightlocal: 1 },
    deepDive: { en: "GPT-4o auto-audits each lead's website (SEO, design, conversion gaps) and turns the findings into a personalised opener — no Clay-style enrichment recipes to maintain.",
                pt: "GPT-4o audita automaticamente o site de cada lead (SEO, design, conversão) e transforma os achados em uma abertura personalizada — sem precisar manter recipes estilo Clay.",
                es: "GPT-4o audita automáticamente el sitio de cada lead (SEO, diseño, conversión) y convierte los hallazgos en un opener personalizado — sin recetas estilo Clay que mantener." } },
  { key: "gpt",      i18n: { en: "GPT Outreach",             pt: "Outreach com GPT",            es: "Outreach con GPT" },
    icon: Sparkles, vals: { alp: 2, apollo: 1, instantly: 1, close: 0, clay: 2, ghl: 1, lemlist: 1, d7: 0, brightlocal: 0 },
    deepDive: { en: "Drafts subject lines, opening hooks and replies grounded in the AI audit — far beyond the {{firstName}} variable trick used by Apollo / Lemlist.",
                pt: "Gera subject lines, hooks e respostas baseadas na auditoria de IA — muito além do truque {{firstName}} usado por Apollo / Lemlist.",
                es: "Genera subject lines, hooks y respuestas basadas en la auditoría de IA — mucho más que el truco {{firstName}} de Apollo / Lemlist." } },
  { key: "wa",       i18n: { en: "WhatsApp Automation",      pt: "Automação WhatsApp",          es: "Automatización WhatsApp" },
    icon: MessageCircle, vals: { alp: 2, apollo: 0, instantly: 0, close: 0, clay: 0, ghl: 2, lemlist: 0, d7: 0, brightlocal: 0 },
    deepDive: { en: "Native WhatsApp sequencing built into the same pipeline — only GoHighLevel matches this, but they don't generate the leads or audits.",
                pt: "Sequenciamento WhatsApp nativo no mesmo pipeline — só o GoHighLevel iguala isso, mas eles não geram os leads nem as auditorias.",
                es: "Secuenciación WhatsApp nativa en el mismo pipeline — solo GoHighLevel iguala esto, pero no genera los leads ni las auditorías." } },
  { key: "crm",      i18n: { en: "Pipeline CRM",             pt: "CRM com Pipeline",            es: "CRM con Pipeline" },
    icon: Layers, vals: { alp: 2, apollo: 1, instantly: 0, close: 2, clay: 0, ghl: 2, lemlist: 0, d7: 0, brightlocal: 0 },
    deepDive: { en: "Visual Kanban + dedicated Gov pipeline replacing Close CRM + GoHighLevel — and you don't lose the prospecting context (every lead carries its source, audit and outreach history).",
                pt: "Kanban visual + pipeline Gov dedicado substituindo Close CRM + GoHighLevel — e você não perde o contexto de prospecção (cada lead carrega origem, auditoria e histórico de outreach).",
                es: "Kanban visual + pipeline Gov dedicado reemplazando Close CRM + GoHighLevel — y no pierdes el contexto de prospección (cada lead conserva origen, auditoría e historial)." } },
  { key: "follow",   i18n: { en: "AI Follow-Up",             pt: "Follow-up com IA",            es: "Follow-up con IA" },
    icon: Send, vals: { alp: 2, apollo: 1, instantly: 2, close: 1, clay: 1, ghl: 2, lemlist: 1, d7: 0, brightlocal: 0 },
    deepDive: { en: "Reply intelligence + auto-drafted answers across email, WhatsApp and SMS — Instantly only does email, Lemlist needs Zapier glue.",
                pt: "Reply intelligence + respostas automáticas em email, WhatsApp e SMS — Instantly só faz email, Lemlist precisa de Zapier.",
                es: "Reply intelligence + respuestas automáticas en email, WhatsApp y SMS — Instantly solo hace email, Lemlist requiere Zapier." } },
  { key: "multi",    i18n: { en: "Multi-channel Sequences",  pt: "Sequências Multicanal",       es: "Secuencias Multicanal" },
    icon: Workflow, vals: { alp: 2, apollo: 1, instantly: 1, close: 2, clay: 1, ghl: 2, lemlist: 1, d7: 0, brightlocal: 0 },
    deepDive: { en: "Email + WhatsApp + SMS in one sequence engine. Apollo/Instantly/Lemlist top out at email; GoHighLevel needs you to wire it manually.",
                pt: "Email + WhatsApp + SMS em uma única engine. Apollo/Instantly/Lemlist param no email; GoHighLevel exige cabeação manual.",
                es: "Email + WhatsApp + SMS en un solo motor. Apollo/Instantly/Lemlist se quedan en email; GoHighLevel requiere cableado manual." } },
  { key: "local",    i18n: { en: "Local Lead Generation",    pt: "Geração de Leads Locais",     es: "Generación de Leads Locales" },
    icon: Target, vals: { alp: 2, apollo: 0, instantly: 0, close: 0, clay: 0, ghl: 1, lemlist: 0, d7: 2, brightlocal: 2 },
    deepDive: { en: "Hyper-local discovery (city/radius/sector/NAICS) shipped with intent signals — D7 + BrightLocal stop at the list, AgenteLead Pro takes you to closed deals.",
                pt: "Descoberta hyper-local (cidade/raio/setor/NAICS) com intent signals — D7 + BrightLocal param na lista, AgenteLead Pro te leva ao fechamento.",
                es: "Descubrimiento hyper-local (ciudad/radio/sector/NAICS) con señales de intención — D7 + BrightLocal se quedan en la lista, AgenteLead Pro te lleva al cierre." } },
  { key: "ease",     i18n: { en: "Ease of Use",              pt: "Facilidade de Uso",           es: "Facilidad de Uso" },
    icon: Star, vals: { alp: 2, apollo: 1, instantly: 2, close: 2, clay: 0, ghl: 0, lemlist: 2, d7: 1, brightlocal: 1 },
    deepDive: { en: "Single login, single workspace, single pricing tier. No Zapier glue, no agency-only setup like GoHighLevel, no Clay-style learning curve.",
                pt: "Um login, um workspace, um pricing. Sem Zapier, sem setup agency-only como GoHighLevel, sem curva Clay.",
                es: "Un login, un workspace, un precio. Sin Zapier, sin configuración agency-only como GoHighLevel, sin curva tipo Clay." } },
  { key: "auto",     i18n: { en: "Automation Level",         pt: "Nível de Automação",          es: "Nivel de Automatización" },
    icon: Zap, vals: { alp: 2, apollo: 1, instantly: 2, close: 1, clay: 2, ghl: 2, lemlist: 1, d7: 0, brightlocal: 0 },
    deepDive: { en: "AI SDR runs Find → Audit → Outreach → Follow-up unattended. Most competitors automate one step at most.",
                pt: "SDR de IA executa Find → Audit → Outreach → Follow-up sem supervisão. A maior parte dos concorrentes automatiza uma etapa só.",
                es: "El SDR con IA ejecuta Find → Audit → Outreach → Follow-up sin supervisión. La mayoría de competidores automatiza una sola etapa." } },
];

// Battlecard rows — each cell holds {alp, apollo, instantly, close, clay, ghl}
const BATTLECARD = [
  { key: "lead",   icon: MapPin, title: { en: "Lead Generation",      pt: "Geração de Leads",        es: "Generación de Leads" },
    rows: { alp: { en: "Google Maps + AI enrichment to find high-intent local businesses.", pt: "Google Maps + enriquecimento com IA para encontrar negócios locais com alta intenção.", es: "Google Maps + enriquecimiento con IA para hallar negocios locales con alta intención." },
            apollo: { en: "B2B database with contacts & accounts.", pt: "Base B2B com contatos e contas.", es: "Base B2B con contactos y cuentas." },
            instantly: { en: "No lead database. You import leads.", pt: "Sem base de leads. Você importa.", es: "Sin base de leads. Tú importas." },
            close: { en: "No lead generation capabilities.", pt: "Sem geração de leads.", es: "Sin generación de leads." },
            clay: { en: "Powerful data enrichment.", pt: "Enriquecimento de dados potente.", es: "Enriquecimiento de datos potente." },
            ghl: { en: "Limited lead-gen capabilities.", pt: "Capacidade limitada de geração.", es: "Capacidad limitada de generación." } } },
  { key: "ai",     icon: Wand2, title: { en: "AI & Personalization",  pt: "IA & Personalização",     es: "IA y Personalización" },
    rows: { alp: { en: "AI business audits + personalized messages that get replies.", pt: "Auditorias com IA + mensagens personalizadas que recebem resposta.", es: "Auditorías con IA + mensajes personalizados con respuesta real." },
            apollo: { en: "Basic personalization with variables.", pt: "Personalização básica com variáveis.", es: "Personalización básica con variables." },
            instantly: { en: "Personalization at scale.", pt: "Personalização em escala.", es: "Personalización a escala." },
            close: { en: "No AI personalization.", pt: "Sem IA para personalização.", es: "Sin IA para personalización." },
            clay: { en: "Advanced AI workflows.", pt: "Workflows de IA avançados.", es: "Flujos de IA avanzados." },
            ghl: { en: "Basic personalization.", pt: "Personalização básica.", es: "Personalización básica." } } },
  { key: "out",    icon: Send, title: { en: "Outreach & Engagement",  pt: "Outreach & Engajamento",  es: "Outreach y Compromiso" },
    rows: { alp: { en: "Multi-channel (Email, WhatsApp, SMS) with automated follow-ups.", pt: "Multicanal (Email, WhatsApp, SMS) com follow-ups automatizados.", es: "Multicanal (Email, WhatsApp, SMS) con follow-ups automatizados." },
            apollo: { en: "Email only (sequences).", pt: "Apenas Email (sequências).", es: "Solo Email (secuencias)." },
            instantly: { en: "Email only (sequences).", pt: "Apenas Email (sequências).", es: "Solo Email (secuencias)." },
            close: { en: "Email, Calls, SMS (manual or basic).", pt: "Email, ligações, SMS (manual ou básico).", es: "Email, llamadas, SMS (manual o básico)." },
            clay: { en: "Depends on integrations.", pt: "Depende de integrações.", es: "Depende de integraciones." },
            ghl: { en: "Email, SMS, WhatsApp.", pt: "Email, SMS, WhatsApp.", es: "Email, SMS, WhatsApp." } } },
  { key: "crm",    icon: Layers, title: { en: "CRM & Pipeline",       pt: "CRM & Pipeline",          es: "CRM y Pipeline" },
    rows: { alp: { en: "Visual pipeline built for outbound sales teams.", pt: "Pipeline visual feito para times de outbound.", es: "Pipeline visual diseñado para equipos outbound." },
            apollo: { en: "Basic CRM (in beta).", pt: "CRM básico (em beta).", es: "CRM básico (en beta)." },
            instantly: { en: "No CRM. Outreach only.", pt: "Sem CRM. Só outreach.", es: "Sin CRM. Solo outreach." },
            close: { en: "Powerful CRM built for closing deals.", pt: "CRM potente para fechar negócios.", es: "CRM potente para cerrar tratos." },
            clay: { en: "No native CRM. Use integrations.", pt: "Sem CRM nativo. Use integrações.", es: "Sin CRM nativo. Usa integraciones." },
            ghl: { en: "Built-in CRM & pipelines.", pt: "CRM e pipelines embutidos.", es: "CRM y pipelines integrados." } } },
  { key: "auto",   icon: Cpu, title: { en: "Automation",              pt: "Automação",                es: "Automatización" },
    rows: { alp: { en: "AI SDR + smart sequences + auto follow-ups across channels.", pt: "SDR com IA + sequências inteligentes + follow-ups automáticos.", es: "SDR con IA + secuencias inteligentes + follow-ups automáticos." },
            apollo: { en: "Sequences + basic automation.", pt: "Sequências + automação básica.", es: "Secuencias + automatización básica." },
            instantly: { en: "Highly automated email sequences.", pt: "Sequências de email altamente automatizadas.", es: "Secuencias de email altamente automatizadas." },
            close: { en: "Workflow automations for sales.", pt: "Automações de workflow para vendas.", es: "Automatizaciones de workflow para ventas." },
            clay: { en: "Advanced automations (with learning curve).", pt: "Automações avançadas (com curva de aprendizado).", es: "Automatizaciones avanzadas (con curva de aprendizaje)." },
            ghl: { en: "Advanced automations.", pt: "Automações avançadas.", es: "Automatizaciones avanzadas." } } },
  { key: "ease",   icon: Star, title: { en: "Ease of Use",            pt: "Facilidade de Uso",        es: "Facilidad de Uso" },
    rows: { alp: { en: "All-in-one. Easy to set up and start closing.", pt: "Tudo-em-um. Setup rápido e já começa a fechar.", es: "Todo-en-uno. Configuración rápida y comienza a cerrar." },
            apollo: { en: "Moderate. Learning curve.", pt: "Moderado. Curva de aprendizado.", es: "Moderado. Curva de aprendizaje." },
            instantly: { en: "Easy for email outreach.", pt: "Fácil para outreach por email.", es: "Fácil para outreach por email." },
            close: { en: "Easy to moderate.", pt: "Fácil a moderado.", es: "Fácil a moderado." },
            clay: { en: "Steeper learning curve.", pt: "Curva de aprendizado mais íngreme.", es: "Curva de aprendizaje más pronunciada." },
            ghl: { en: "Moderate. More complex setup.", pt: "Moderado. Setup mais complexo.", es: "Moderado. Configuración más compleja." } } },
  { key: "best",   icon: UsersIcon, title: { en: "Best For",          pt: "Ideal Para",               es: "Ideal Para" },
    rows: { alp: { en: "Agencies, SDRs, consultants and local service businesses that want more deals.", pt: "Agências, SDRs, consultores e negócios locais que querem mais fechamentos.", es: "Agencias, SDRs, consultores y negocios locales que quieren más cierres." },
            apollo: { en: "B2B sales teams scaling outbound.", pt: "Times B2B escalando outbound.", es: "Equipos B2B escalando outbound." },
            instantly: { en: "Teams focused on cold email at scale.", pt: "Times focados em cold email em escala.", es: "Equipos enfocados en cold email a escala." },
            close: { en: "Inside sales teams that close a lot of deals.", pt: "Times de inside sales que fecham muitos negócios.", es: "Equipos de inside sales que cierran muchos tratos." },
            clay: { en: "Data-driven teams & power users.", pt: "Times data-driven e power users.", es: "Equipos data-driven y power users." },
            ghl: { en: "Agencies & service businesses.", pt: "Agências e empresas de serviço.", es: "Agencias y empresas de servicio." } } },
];

// Quadrant positions (x: ease 0..100, y: AI 0..100) + per-vendor strength
// & limitation rendered inside hover tooltips (Block C).
const QUADRANT = [
  { key: "alp", name: "AgenteLead Pro", x: 78, y: 86, color: "#A855F7", featured: true,
    strength: { en: "Highest AI + automation in the easiest-to-use UX. Find → Audit → Outreach → Close in one flow.",
                pt: "Maior IA + automação na UX mais simples. Find → Audit → Outreach → Close num só fluxo.",
                es: "Mayor IA + automatización en la UX más simple. Find → Audit → Outreach → Close en un solo flujo." },
    limitation: { en: "Younger product than Apollo or Close — but moving fastest.",
                  pt: "Produto mais novo que Apollo ou Close — mas evoluindo mais rápido.",
                  es: "Producto más joven que Apollo o Close — pero el que más rápido evoluciona." } },
  { key: "apollo",     name: "Apollo.io",      x: 65, y: 60, color: "#0EA5E9",
    strength: { en: "Strong B2B database & sequences.", pt: "Base B2B forte e boas sequências.", es: "Base B2B fuerte y buenas secuencias." },
    limitation: { en: "No local lead generation, no AI audits.", pt: "Sem geração local, sem auditorias com IA.", es: "Sin generación local, sin auditorías con IA." } },
  { key: "clay",       name: "Clay",           x: 28, y: 90, color: "#F97316",
    strength: { en: "Top-tier enrichment & data workflows.", pt: "Enriquecimento e workflows top.", es: "Enriquecimiento y flujos top." },
    limitation: { en: "Steep learning curve, no native CRM.", pt: "Curva de aprendizado íngreme, sem CRM nativo.", es: "Curva pronunciada, sin CRM nativo." } },
  { key: "instantly",  name: "Instantly.ai",    x: 82, y: 32, color: "#FACC15",
    strength: { en: "Easy cold-email at scale.", pt: "Cold email em escala, fácil.", es: "Cold email a escala, fácil." },
    limitation: { en: "Email only, no leads, no CRM.", pt: "Só email, sem leads, sem CRM.", es: "Solo email, sin leads, sin CRM." } },
  { key: "lemlist",    name: "Lemlist",         x: 75, y: 28, color: "#14B8A6",
    strength: { en: "Email personalisation pioneer.", pt: "Pioneiro em personalização de email.", es: "Pionero en personalización de email." },
    limitation: { en: "Email-centric, weak on CRM & multi-channel.", pt: "Centrado em email, fraco em CRM e multicanal.", es: "Centrado en email, débil en CRM y multicanal." } },
  { key: "ghl",        name: "GoHighLevel",     x: 55, y: 24, color: "#22D3EE",
    strength: { en: "All-in-one toolkit for agencies.", pt: "Tudo-em-um para agências.", es: "Todo-en-uno para agencias." },
    limitation: { en: "No native lead sourcing or AI audits.", pt: "Sem sourcing nativo nem auditorias.", es: "Sin sourcing nativo ni auditorías." } },
  { key: "close",      name: "Close CRM",       x: 70, y: 18, color: "#94A3B8",
    strength: { en: "Premium inside-sales CRM.", pt: "CRM premium para inside sales.", es: "CRM premium para inside sales." },
    limitation: { en: "No prospecting, no AI personalization.", pt: "Sem prospecção, sem IA.", es: "Sin prospección, sin IA." } },
  { key: "d7",         name: "D7 Lead Finder",  x: 30, y: 18, color: "#EF4444",
    strength: { en: "Cheap Maps scraping.", pt: "Scraping Maps barato.", es: "Scraping Maps barato." },
    limitation: { en: "Just a list — no audits, outreach, or CRM.", pt: "Só uma lista — sem auditorias, outreach ou CRM.", es: "Solo una lista — sin auditorías, outreach ni CRM." } },
  { key: "brightlocal", name: "BrightLocal",    x: 22, y: 12, color: "#F472B6",
    strength: { en: "Local SEO reporting.", pt: "Reports de SEO local.", es: "Reportes de SEO local." },
    limitation: { en: "Not a sales tool — no outreach.", pt: "Não é ferramenta de vendas — sem outreach.", es: "No es herramienta de ventas — sin outreach." } },
];

const KPIS = [
  { v: "3-10×", l: { en: "More Qualified Leads",        pt: "Mais Leads Qualificados",     es: "Más Leads Calificados" } },
  { v: "70%+",  l: { en: "Response Rate Increase",      pt: "Aumento na Taxa de Resposta", es: "Aumento en Tasa de Respuesta" } },
  { v: "90%",   l: { en: "Time Saved",                  pt: "Tempo Economizado",           es: "Tiempo Ahorrado" } },
  { v: "2-5×",  l: { en: "Pipeline Growth",             pt: "Crescimento do Pipeline",     es: "Crecimiento del Pipeline" } },
];

const STEPS = [
  { icon: Search,        en: "Find Leads",              pt: "Encontrar Leads",            es: "Encontrar Leads" },
  { icon: Sparkles,      en: "Enrich with AI",          pt: "Enriquecer com IA",          es: "Enriquecer con IA" },
  { icon: Wand2,         en: "Generate Business Audit", pt: "Gerar Auditoria de Negócio", es: "Generar Auditoría de Negocio" },
  { icon: Bot,           en: "AI Outreach",             pt: "Outreach com IA",            es: "Outreach con IA" },
  { icon: MessageCircle, en: "Multi-channel Follow-up", pt: "Follow-up Multicanal",       es: "Follow-up Multicanal" },
  { icon: Layers,        en: "Pipeline Management",     pt: "Gestão de Pipeline",         es: "Gestión de Pipeline" },
  { icon: Rocket,        en: "Close & Grow",            pt: "Fechar & Crescer",           es: "Cerrar y Crecer" },
];


function CapCell({ v }) {
  if (v === 2) return <Check className="w-4 h-4 text-emerald-300 mx-auto" strokeWidth={3} />;
  if (v === 1) return <span className="inline-block w-4 h-4 rounded-full bg-amber-400/20 border border-amber-400/60 mx-auto" />;
  return <Minus className="w-3.5 h-3.5 text-white/30 mx-auto" />;
}

export default function StrategicComparison({ lang = "en" }) {
  const t = T[lang] || T.en;

  return (
    <section data-testid="strategic-comparison" className="relative py-24 lg:py-32 overflow-hidden"
      style={{ background: "radial-gradient(ellipse at top, #1A0E36 0%, #0A0817 60%, #050410 100%)" }}>
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
             style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 60%)" }} />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full opacity-25 blur-3xl"
             style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 60%)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 space-y-28">

        {/* ───────── Block A — SaaS comparison table ───────── */}
        <Reveal><BlockA t={t} lang={lang} /></Reveal>

        {/* ───────── Block B — Battlecard ───────── */}
        <Reveal delay={120}><BlockB t={t} lang={lang} /></Reveal>

        {/* ───────── Block C — Quadrant ───────── */}
        <Reveal delay={180}><BlockC t={t} lang={lang} /></Reveal>

        {/* ───────── Block D — Workflow + CTA ───────── */}
        <Reveal delay={240}><BlockD t={t} lang={lang} /></Reveal>
      </div>
    </section>
  );
}

/* ============================================================
   BLOCK A — Comparison Table (interactive: click row → modal)
   ============================================================ */
function BlockA({ t, lang }) {
  const [activeCap, setActiveCap] = useState(null);
  return (
    <div data-testid="strategic-block-a">
      <div className="text-center max-w-3xl mx-auto mb-10">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-purple-300 mb-3">
          <Sparkles className="w-3.5 h-3.5" /> A · Visual Comparison
        </span>
        <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] font-extrabold text-white leading-[1.05] mb-3">
          {t.aTitle.split("AgenteLead Pro").map((part, i, arr) => (
            <span key={i}>
              {part}
              {i < arr.length - 1 && (
                <span className="bg-gradient-to-r from-fuchsia-400 to-purple-300 bg-clip-text text-transparent">AgenteLead Pro</span>
              )}
            </span>
          ))}
        </h2>
        <p className="text-[14.5px] text-white/65 leading-relaxed">{t.aSubtitle}</p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-purple-500/20 backdrop-blur-sm shadow-[0_30px_120px_-30px_rgba(124,58,237,0.5)]"
        style={{ background: "linear-gradient(180deg, rgba(15,8,32,0.85) 0%, rgba(8,4,18,0.95) 100%)" }}>
        <table className="w-full text-left text-[12.5px] min-w-[920px]" data-testid="comparison-table">
          <thead>
            <tr>
              <th className="p-4 text-[11px] uppercase tracking-wider text-white/45 font-bold">Capability</th>
              {VENDORS.map((v) => (
                <th key={v.key}
                  className={`p-3 text-center ${v.featured ? "relative" : ""}`}
                  style={v.featured ? { background: "linear-gradient(180deg, rgba(168,85,247,0.18) 0%, rgba(168,85,247,0.06) 100%)" } : {}}>
                  {v.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white text-[9px] font-bold uppercase tracking-wider whitespace-nowrap shadow-lg">
                      <Crown className="w-2.5 h-2.5" /> {t.aBadge}
                    </div>
                  )}
                  <VendorMark vendor={v} />
                  <div className={`text-[12px] font-bold ${v.featured ? "text-fuchsia-200" : "text-white/85"}`}>{v.name}</div>
                  {v.featured && <div className="text-[9.5px] text-purple-300/70 mt-0.5">{t.aBadgeSub}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAPS.map((cap) => (
              <tr key={cap.key}
                  className="border-t border-white/5 hover:bg-fuchsia-500/[0.06] cursor-pointer transition-colors group"
                  onClick={() => setActiveCap(cap)}
                  data-testid={`comparison-row-${cap.key}`}>
                <td className="p-3 text-white/85 font-medium">
                  <span className="inline-flex items-center gap-2">
                    <cap.icon className="w-3.5 h-3.5 text-purple-300/80" />
                    {cap.i18n[lang] || cap.i18n.en}
                    <span className="opacity-0 group-hover:opacity-100 text-[10px] text-fuchsia-300 transition-opacity">↗</span>
                  </span>
                </td>
                {VENDORS.map((v) => (
                  <td key={v.key} className="p-3 text-center"
                      style={v.featured ? { background: "rgba(168,85,247,0.08)" } : {}}>
                    <CapCell v={cap.vals[v.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Click hint + Legend */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-center justify-center mt-5 text-[11.5px] text-white/55">
        <span className="inline-flex items-center gap-1.5 text-fuchsia-300/85">
          <Sparkles className="w-3 h-3" /> {t.aClickHint}
        </span>
        <span className="hidden sm:inline text-white/20">|</span>
        <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" />{t.legendStrong}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400/20 border border-amber-400/60" />{t.legendPartial}</span>
        <span className="inline-flex items-center gap-1.5"><Minus className="w-3.5 h-3.5 text-white/30" />{t.legendNo}</span>
      </div>

      {activeCap && <CapabilityModal cap={activeCap} t={t} lang={lang} onClose={() => setActiveCap(null)} />}
    </div>
  );
}


/**
 * CapabilityModal — opens when a row in Block A is clicked. Shows the
 * deep-dive copy explaining WHY AgenteLead Pro replaces the competitors
 * marked as `vals === 2` (strong) or 1 (partial) for that capability.
 */
function CapabilityModal({ cap, t, lang, onClose }) {
  // ESC closes the modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const replacedVendors = VENDORS.filter((v) => v.key !== "alp" && cap.vals[v.key] >= 1);

  return (
    <div className="fixed inset-0 z-[2147483600] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
         onClick={onClose} data-testid="capability-modal">
      <div className="relative max-w-2xl w-full rounded-2xl border border-fuchsia-400/40 p-7 backdrop-blur-xl"
           style={{ background: "linear-gradient(160deg, rgba(30,15,55,0.95), rgba(10,5,25,0.98))",
                    boxShadow: "0 40px 100px -30px rgba(168,85,247,0.6)" }}
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white flex items-center justify-center"
          data-testid="capability-modal-close" title={t.aModalClose}>
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500/30 to-purple-500/20 border border-fuchsia-400/40 flex items-center justify-center">
            <cap.icon className="w-5 h-5 text-fuchsia-200" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-fuchsia-300/85 font-bold">AgenteLead Pro</div>
            <h3 className="text-[20px] font-extrabold text-white leading-tight">{cap.i18n[lang] || cap.i18n.en}</h3>
          </div>
        </div>

        <p className="text-[13.5px] text-white/85 leading-relaxed mb-5">
          {cap.deepDive[lang] || cap.deepDive.en}
        </p>

        {replacedVendors.length > 0 && (
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/55 font-bold mb-2">
              {t.aModalReplaces}
            </div>
            <div className="flex flex-wrap gap-2">
              {replacedVendors.map((v) => (
                <span key={v.key}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.04] text-[11.5px] text-white/80">
                  {v.logo && (
                    <img src={`https://cdn.simpleicons.org/${v.logo}`} alt="" aria-hidden
                      className="w-3.5 h-3.5 object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                  {v.name}
                  {cap.vals[v.key] === 1 && (
                    <span className="text-[9px] text-amber-300/80 ml-0.5">(partial)</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   BLOCK B — Battlecard
   ============================================================ */
function BlockB({ t, lang }) {
  const cols = ["alp", "apollo", "instantly", "close", "clay", "ghl"];
  const colLabels = { alp: "AGENTELEAD PRO", apollo: "APOLLO.IO", instantly: "INSTANTLY.AI", close: "CLOSE CRM", clay: "CLAY", ghl: "GOHIGHLEVEL" };
  return (
    <div data-testid="strategic-block-b">
      <div className="text-center max-w-3xl mx-auto mb-10">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-purple-300 mb-3">
          <Trophy className="w-3.5 h-3.5" /> B · Battlecard
        </span>
        <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] font-extrabold text-white leading-[1.05] mb-3">{t.bTitle}</h2>
        <p className="text-[14.5px] text-white/65">{t.bSubtitle}</p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10 backdrop-blur-sm"
           style={{ background: "linear-gradient(180deg, rgba(15,8,32,0.85), rgba(8,4,18,0.95))" }}>
        <table className="w-full text-left text-[12.5px] min-w-[860px]" data-testid="battlecard-table">
          <thead>
            <tr>
              <th className="p-4 text-[10px] uppercase tracking-[0.16em] text-white/45 font-bold w-[18%]">Category</th>
              {cols.map((c) => (
                <th key={c} className={`p-3 text-[10.5px] uppercase tracking-wider ${c === "alp" ? "text-fuchsia-200 bg-fuchsia-500/10" : "text-white/55"} font-bold`}>{colLabels[c]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BATTLECARD.map((b) => (
              <tr key={b.key} className="border-t border-white/5 align-top">
                <td className="p-4 font-bold text-white/85">
                  <span className="inline-flex items-center gap-2">
                    <b.icon className="w-3.5 h-3.5 text-purple-300/80" />
                    {b.title[lang] || b.title.en}
                  </span>
                </td>
                {cols.map((c) => (
                  <td key={c} className={`p-4 leading-relaxed text-[12px] ${c === "alp" ? "bg-fuchsia-500/[0.06] text-white border-l border-r border-fuchsia-500/30" : "text-white/65"}`}>
                    {b.rows[c]?.[lang] || b.rows[c]?.en || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Callout cards */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="rounded-2xl border border-amber-400/30 p-5 backdrop-blur-sm"
             style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.10), rgba(168,85,247,0.05))" }}
             data-testid="battlecard-key-takeaway">
          <div className="flex items-center gap-2.5 mb-2">
            <Trophy className="w-5 h-5 text-amber-300" />
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-200">{t.bKey}</span>
          </div>
          <p className="text-[13px] text-white/80 leading-relaxed">{t.bCallout}</p>
        </div>
        <div className="rounded-2xl border border-purple-500/30 p-5 backdrop-blur-sm"
             style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.12), rgba(37,99,235,0.05))" }}>
          <div className="flex items-center gap-2.5 mb-2">
            <UsersIcon className="w-5 h-5 text-purple-300" />
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-purple-200">{t.bIdealUserTitle}</span>
          </div>
          <p className="text-[13px] text-white/80 leading-relaxed">{t.bIdealUserBody}</p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   BLOCK C — Strategic Quadrant
   ============================================================ */
function BlockC({ t, lang }) {
  return (
    <div data-testid="strategic-block-c">
      <div className="text-center max-w-3xl mx-auto mb-10">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-purple-300 mb-3">
          <BarChart3 className="w-3.5 h-3.5" /> C · Strategic Position
        </span>
        <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] font-extrabold text-white leading-[1.05] mb-3">{t.cTitle}</h2>
        <p className="text-[14.5px] text-white/65">{t.cSubtitle}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* The quadrant */}
        <div className="lg:col-span-2 relative rounded-2xl border border-purple-500/20 backdrop-blur-sm aspect-[4/3] overflow-hidden"
             style={{ background: "radial-gradient(ellipse at center, rgba(124,58,237,0.10) 0%, rgba(8,4,18,0.95) 75%)" }}
             data-testid="strategic-quadrant">
          {/* Axes */}
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="axisGlow" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#A855F7" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#A855F7" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            <line x1="50" y1="0" x2="50" y2="100" stroke="rgba(168,85,247,0.18)" strokeWidth="0.2" strokeDasharray="0.8 0.8" />
            <line x1="0" y1="50" x2="100" y2="50" stroke="rgba(168,85,247,0.18)" strokeWidth="0.2" strokeDasharray="0.8 0.8" />
          </svg>

          {/* Quadrant labels */}
          <div className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-bold text-rose-300/80">{t.cQ.powerful}</div>
          <div className="absolute top-3 right-3 text-[10px] uppercase tracking-wider font-bold text-emerald-300">{t.cQ.leaders}</div>
          <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-wider font-bold text-white/40">{t.cQ.limited}</div>
          <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-wider font-bold text-amber-300/80">{t.cQ.easy}</div>

          {/* Axis labels */}
          <div className="absolute left-2 top-1/2 -translate-y-1/2 -rotate-90 text-[9.5px] uppercase tracking-[0.16em] font-bold text-purple-300/70 origin-center whitespace-nowrap">
            ↑ {t.cAxisY}
          </div>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[9.5px] uppercase tracking-[0.16em] font-bold text-purple-300/70 whitespace-nowrap">
            {t.cAxisX} →
          </div>

          {/* Vendor dots — hover shows strength + limitation tooltip */}
          {QUADRANT.map((q) => (
            <div key={q.key}
              className={`absolute -translate-x-1/2 -translate-y-1/2 group ${q.featured ? "z-10" : ""}`}
              style={{ left: `${q.x}%`, top: `${100 - q.y}%` }}
              data-testid={`quadrant-${q.key}`}>
              <div className={`relative ${q.featured ? "w-12 h-12" : "w-3 h-3"} rounded-full transition-transform group-hover:scale-125`}
                   style={q.featured
                     ? { background: "radial-gradient(circle, #C084FC 0%, #7C3AED 70%)", boxShadow: "0 0 32px 8px rgba(168,85,247,0.55)" }
                     : { background: q.color, boxShadow: `0 0 12px ${q.color}88` }}>
                {q.featured && (
                  <span className="absolute inset-0 flex items-center justify-center text-white"><Crown className="w-5 h-5" /></span>
                )}
              </div>
              <div className={`absolute left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap text-[10.5px] font-bold ${q.featured ? "text-fuchsia-200" : "text-white/75"}`}>
                {q.name}
              </div>
              {/* Tooltip — strength + limitation */}
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20"
                   data-testid={`quadrant-tooltip-${q.key}`}>
                <div className="rounded-lg border border-fuchsia-400/30 bg-[#0A0418]/95 backdrop-blur-md p-3 shadow-2xl">
                  <div className="text-[11.5px] font-bold text-white mb-1">{q.name}</div>
                  <div className="text-[10.5px] text-emerald-300 mb-1.5 leading-relaxed">
                    <span className="opacity-60 mr-1">✓</span>{(q.strength || {})[lang] || (q.strength || {}).en || ""}
                  </div>
                  <div className="text-[10.5px] text-rose-300/85 leading-relaxed">
                    <span className="opacity-60 mr-1">✗</span>{(q.limitation || {})[lang] || (q.limitation || {}).en || ""}
                  </div>
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 rotate-45 bg-[#0A0418] border-r border-b border-fuchsia-400/30" />
              </div>
            </div>
          ))}
        </div>

        {/* Side insights */}
        <div className="space-y-2.5" data-testid="quadrant-insights">
          {[
            { name: "AgenteLead Pro", color: "#C084FC", strength: { en: "Combines high automation, AI, and ease of use.", pt: "Combina alta automação, IA e facilidade de uso.", es: "Combina alta automatización, IA y facilidad de uso." } },
            { name: "Apollo.io", color: "#0EA5E9", strength: { en: "Strong contender for B2B data & outreach at scale.", pt: "Forte para dados B2B e outreach em escala.", es: "Fuerte para datos B2B y outreach a escala." } },
            { name: "Clay", color: "#F97316", strength: { en: "Top-tier enrichment & AI workflows, but complex.", pt: "Enriquecimento e workflows com IA top, mas complexos.", es: "Enriquecimiento top y flujos IA, pero complejos." } },
            { name: "Instantly.ai", color: "#FACC15", strength: { en: "Easy to use. Focus on cold email at scale.", pt: "Fácil de usar. Foco em cold email em escala.", es: "Fácil de usar. Enfoque en cold email a escala." } },
            { name: "GoHighLevel", color: "#22D3EE", strength: { en: "All-in-one for agencies, not specialised in lead gen.", pt: "Tudo-em-um para agências, não especializado em lead gen.", es: "Todo-en-uno para agencias, no especializado en lead gen." } },
            { name: "Close CRM", color: "#94A3B8", strength: { en: "Excellent CRM. Lacks lead sourcing.", pt: "CRM excelente. Falta lead sourcing.", es: "CRM excelente. Falta sourcing de leads." } },
            { name: "D7 Lead Finder", color: "#EF4444", strength: { en: "Niche tool. Good Maps scraping, lacks automation.", pt: "Ferramenta de nicho. Bom scraping Maps, sem automação.", es: "Herramienta de nicho. Buen scraping Maps, sin automatización." } },
            { name: "BrightLocal", color: "#F472B6", strength: { en: "Niche tool. Great for local SEO, not sales outreach.", pt: "Ferramenta de nicho. Ótima para SEO local, não outreach.", es: "Herramienta de nicho. Genial para SEO local, no outreach." } },
          ].map((it) => (
            <div key={it.name}
                 className="rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors p-2.5 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="w-2 h-2 rounded-full" style={{ background: it.color, boxShadow: `0 0 8px ${it.color}` }} />
                <span className="font-bold text-[12px] text-white/95">{it.name}</span>
              </div>
              <div className="text-[11px] text-white/60 pl-4 leading-relaxed">{it.strength[lang] || it.strength.en}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   BLOCK D — Workflow + KPIs + CTA
   ============================================================ */
function BlockD({ t, lang }) {
  return (
    <div data-testid="strategic-block-d">
      <div className="text-center max-w-3xl mx-auto mb-10">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-purple-300 mb-3">
          <Workflow className="w-3.5 h-3.5" /> D · How It Works
        </span>
        <h2 className="text-[32px] sm:text-[40px] lg:text-[48px] font-extrabold text-white leading-[1.05] mb-3">{t.dTitle}</h2>
        <p className="text-[14.5px] text-white/65">{t.dSub}</p>
      </div>

      {/* 7-step workflow */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 mb-10" data-testid="strategic-workflow">
        {STEPS.map((s, i) => (
          <div key={s.en}
               className="group relative rounded-xl border border-purple-500/20 bg-white/[0.03] p-3 backdrop-blur-sm transition-all hover:border-fuchsia-400/60 hover:scale-105"
               style={{ animationDelay: `${i * 80}ms` }}>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="w-5 h-5 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
              <s.icon className="w-3.5 h-3.5 text-purple-300 group-hover:text-fuchsia-300 transition-colors" />
            </div>
            <div className="text-[11.5px] text-white/85 font-bold leading-tight">{s[lang] || s.en}</div>
            {i < STEPS.length - 1 && (
              <ChevronRight className="hidden lg:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400/50" />
            )}
          </div>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-12" data-testid="strategic-kpis">
        {KPIS.map((k) => (
          <div key={k.v}
               className="rounded-2xl border border-fuchsia-400/20 p-5 text-center backdrop-blur-sm"
               style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.10), rgba(37,99,235,0.04))" }}>
            <div className="text-[28px] sm:text-[32px] font-extrabold bg-gradient-to-br from-fuchsia-300 to-purple-400 bg-clip-text text-transparent">{k.v}</div>
            <div className="text-[11px] text-white/65 uppercase tracking-wider font-bold mt-1">{k.l[lang] || k.l.en}</div>
          </div>
        ))}
      </div>

      {/* Final CTA card */}
      <div className="relative rounded-2xl border border-purple-500/40 p-8 lg:p-10 text-center overflow-hidden backdrop-blur-sm"
           style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.18) 0%, rgba(37,99,235,0.10) 60%, rgba(168,85,247,0.18) 100%)" }}
           data-testid="strategic-cta">
        <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle at 30% 20%, rgba(192,132,252,0.5), transparent 50%), radial-gradient(circle at 70% 80%, rgba(37,99,235,0.4), transparent 50%)" }} />
        <div className="relative">
          <h3 className="text-[26px] sm:text-[34px] font-extrabold text-white mb-2">{t.dCtaTitle}</h3>
          <p className="text-[13.5px] text-white/70 max-w-xl mx-auto mb-6">{t.dCtaSub}</p>
          <a href="#cta"
             className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 text-white font-bold text-[14px] shadow-[0_20px_50px_-15px_rgba(168,85,247,0.7)] hover:shadow-[0_25px_55px_-15px_rgba(168,85,247,0.9)] transition-shadow"
             data-testid="strategic-cta-button">
            {t.dCta} <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
